import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import {
  CheckIcon,
  PaperClipIcon,
  PencilIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import Pagination from "../../../components/Pagination";
import ModelForm from "./ModelForm";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import PaginationV2 from "../../../components/PaginationV2";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import { useLocation } from "react-router-dom";
import ColumnSearch from "../../../components/ColumnSearch";
import useDialogStore from "../../../stores/useDialogStore";
import Button from "../../../components/Button";

export default function ModelList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeModel, setActiveModel] = useState({});

  const searchParams = new URLSearchParams(location.search);
  const currentPage = parseInt(searchParams.get("page") || "1");
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialogStore();

  // Sorting config
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });

  const user = getUser();

  // Handle column search
  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
  };

  // Handle column sort
  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    let currentOrder = params.get("order");
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      params.set("sortBy", column);
      params.set("order", "desc");
    }
    navigate(`${location.pathname}?${params.toString()}`);
    setSortConfig({ key: column, direction });
  };

  const {
    data: models,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["models", currentPage, location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      params.set("page", currentPage);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?${params.toString()}`
      );
      return response.data.data;
    },
  });

  // Fetch brands for reference
  const { data: brands } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-brands`
      );
      return response.data.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/device-models/${id}`
      ),
    onSuccess: () => {
      refetch();
      closeDialog();
      toast.success("Model deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete model");
    },
  });

  const editCellMutation = useMutation({
    mutationFn: ({ id, field, value }) =>
      axiosInstance.put(
        `${window.REACT_APP_API_ENDPOINT}/device-models/${id}`,
        {
          [field]: value,
        }
      ),
    onSuccess: () => {
      refetch();
      toast.success("Order updated successfully");
    },
    onError: () => {
      toast.error("Failed to update order");
    },
  });

  const handleDelete = async (id) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error("Delete model error:", error);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands?.find((b) => b.id === brandId);
    return brand ? brand.name : "Unknown Brand";
  };
  const getBrandOptions = () => {
    return (
      brands?.map((brand) => ({
        id: brand.id,
        name: brand.name,
      })) || []
    );
  };
  const handleClearFilters = () => {
    navigate(location.pathname);
    // Force refetch when filters are cleared
    queryClient.invalidateQueries(["devices"]);
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>
        <ModelForm
          modelID={activeModel.id}
          updateData={() => refetch()}
          brands={Array.isArray(brands) ? brands : []}
        />

        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">Models</h4>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<XMarkIcon className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="mb-2 lg:mb-0"
                >
                  Clear Filters
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        ID
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Name"
                            columnKey="name"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Name..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Brand"
                            columnKey="brand"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Brand..."
                          />
                        </div>
                      </th>

                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Order"
                            columnKey="order"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Order..."
                          />
                        </div>
                      </th>

                      {user.role === "SuperAdmin" && (
                        <th
                          scope="col"
                          className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                        >
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : models?.data?.length == 0 ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <NoData message="No models found" />
                        </td>
                      </tr>
                    ) : (
                      models?.data?.map((model, index) => (
                        <tr
                          key={model.id}
                          className={`divide-y divide-gray-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }`}
                        >
                          <td className="whitespace-nowrap py-5 pl-8 text-sm ">
                            {model.id}
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableCell
                                value={model.name}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: model.id,
                                    field: "name",
                                    value: newValue.value,
                                  });
                                }}
                                inputType="text"
                                maxLength={24}
                                className="font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <EditableSelectCell
                                value={model.device_brand_id}
                                displayValue={getBrandName(
                                  model.device_brand_id
                                )}
                                options={getBrandOptions()}
                                onSave={(newValue) => {
                                  editCellMutation.mutate({
                                    id: model.id,
                                    field: "device_brand_id",
                                    value: parseInt(newValue.value),
                                  });
                                }}
                                maxLength={17}
                                className="font-medium text-gray-900"
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-8">
                              <div className="flex items-center space-x-2 cursor-pointer">
                                <EditableCell
                                  value={model.order}
                                  onSave={(newValue) => {
                                    editCellMutation.mutate({
                                      id: model.id,
                                      field: "order",
                                      value: newValue.value,
                                    });
                                  }}
                                  inputType="number"
                                  className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-900"
                                />
                              </div>
                            </div>
                          </td>

                          {user.role === "SuperAdmin" && (
                            <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                              <div className="flex items-center pl-8">
                                <Button
                                  variant="outline-danger"
                                  size="icon"
                                  icon={<TrashIcon className="h-4 w-4" />}
                                  onClick={() => {
                                    openDialog({
                                      title: "Delete Model",
                                      content: (
                                        <div className="flex flex-col gap-4">
                                          <p className="text-lg">
                                            Are you sure you want to delete{" "}
                                            <span className="font-bold">
                                              {model.name}
                                            </span>
                                            ?
                                            <br />
                                            <span className="text-red-500 text-sm font-bold">
                                              (This action cannot be undone)
                                            </span>
                                          </p>
                                          <div className="flex items-center justify-end gap-2">
                                            <Button
                                              variant="light"
                                              size="sm"
                                              onClick={() => closeDialog()}
                                            >
                                              Cancel
                                            </Button>
                                            <Button
                                              variant="danger"
                                              size="sm"
                                              icon={
                                                <TrashIcon className="h-4 w-4" />
                                              }
                                              onClick={() => {
                                                handleDelete(model.id);
                                                closeDialog();
                                              }}
                                            >
                                              Delete
                                            </Button>
                                          </div>
                                        </div>
                                      ),
                                    });
                                  }}
                                  className="p-1"
                                />
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={models?.current_page}
                last_page={models?.last_page}
                next_page_url={models?.next_page_url}
                prev_page_url={models?.prev_page_url}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
